import { ConnectError } from '@connectrpc/connect'
import {
    Alert,
    AlertProps,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Snackbar,
    Typography,
} from '@mui/material'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from 'react-oidc-context'
import { v4 as uuid } from 'uuid'
import { ValidationErrorDtoSchema } from '@/generated/Messages/Validation_pb'

const seleniumPrefix = 'feedback-context'

interface SnackbarFeedback extends Required<Pick<AlertProps, 'severity'>> {
    /**
     * `Snackbar` component should be used for information that isn't absolutely relevant to the user.
     * F.e. it isn't vital that the user sees, that the form was successfully sent to the backend.
     */
    variant: 'snackbar'
    title: string
}

interface DialogFeedback {
    /**
     * `Dialog` component should be used for information that is vital to the user.
     * F.e. the backend sends an error, when the data sent lead to validation errors.
     */
    variant: 'dialog'
    title: string
    /**
     * Optional content, otherwise only the title will be displayed.
     */
    content?: React.ReactNode
    /**
     * If passed, the dialog will automatically contain the relevant error information.
     */
    error?: Error
}

export interface FeedbackContextValue {
    /**
     * Pushes a new feedback value onto the context.
     * @param value New feedback that should be displayed
     * @returns
     */
    addFeedback: (value: SnackbarFeedback | DialogFeedback) => void
}

const Context = React.createContext<FeedbackContextValue | null>(null)

/**
 * @returns An optional object that contains the `addFeedback` function.
 */
export const useFeedbackContext = (): Partial<FeedbackContextValue> =>
    React.use(Context) ?? {}

/**
 * Provides the context to the application and also contains the displaying of the feedback values.
 *
 */
export const FeedbackContextProvider: React.FC<React.PropsWithChildren> = ({
    children,
}) => {
    const [state, setState] = React.useState<
        ((SnackbarFeedback | DialogFeedback) & { id: string })[]
    >([])
    // `open` is needed so the snackbar and dialog components don't lose their content, when a new feedback item is selected.
    // This would lead to the nacked snackbar or dialog components being shown for a few 100ms, before they are hidden completely.
    // First the snackbar or dialog is closed and only then the corresponding feedback item is filtered out.
    const [open, setOpen] = React.useState(true)
    const { t } = useTranslation()

    const contextValue = React.useMemo<FeedbackContextValue>(
        () => ({
            addFeedback: value =>
                setState(prev => [...prev, { id: uuid(), ...value }]),
        }),
        []
    )

    // Find the next feedback item to display.
    // Dialog feedback items are moved to the front, since they are more important than snackbar components.
    const [nextFeedback] = state.toSorted(({ variant: a }, { variant: b }) =>
        a === 'dialog' && b === 'dialog' ? 0
        : a === 'dialog' ? -1
        : b === 'dialog' ? 1
        : 0
    )

    const handleClose = () => {
        setOpen(false)

        // Give the snackbar one second, to animate out.
        // Only then filter out the current feedback value, so the next one can be selected.
        setTimeout(() => {
            setState(prev => prev.filter(f => f.id !== nextFeedback?.id))
            setOpen(true)
        }, 1000)
    }

    const isAdmin = useAuth().user?.profile.role === 'Administrator'

    return (
        <Context value={contextValue}>
            {children}

            {nextFeedback?.variant === 'snackbar' && (
                <Snackbar
                    open={open}
                    autoHideDuration={6000}
                    onClose={(_, reason) => {
                        // Don't hide the snackbar item, when a click happens anywhere on the page.
                        if (reason === 'clickaway') {
                            return
                        }

                        handleClose()
                    }}
                    sx={{
                        // Add additional spacing to the bottom, so the snackbar isn't hiding the `BottomAppNavigation`.
                        // Only relevant for non admin users.
                        marginBottom: {
                            xs: !isAdmin ? 7 : 'unset',
                            sm: 'unset',
                        },
                    }}
                >
                    <Alert
                        severity={nextFeedback.severity}
                        variant={'outlined'}
                        onClose={handleClose}
                    >
                        {nextFeedback.title}
                    </Alert>
                </Snackbar>
            )}

            {nextFeedback?.variant === 'dialog' && (
                <Dialog open={open} fullWidth={true} maxWidth={'sm'}>
                    <DialogTitle>{nextFeedback.title}</DialogTitle>
                    {(nextFeedback.content || nextFeedback.error) && (
                        <DialogContent>
                            {nextFeedback.content}

                            <br />

                            {nextFeedback.error instanceof ConnectError ?
                                nextFeedback.error
                                    .findDetails(ValidationErrorDtoSchema)
                                    .map(vm => (
                                        <Typography key={vm.propertyName}>
                                            {vm.errorMessage}
                                        </Typography>
                                    ))
                            :   nextFeedback.error?.message}
                        </DialogContent>
                    )}
                    <DialogActions>
                        <Button
                            onClick={handleClose}
                            data-selenium={`${seleniumPrefix}-close`}
                        >
                            {t('common.close')}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </Context>
    )
}
